// AccountReferrals.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Card,
  Typography,
  Space,
  Button,
  message,
  Avatar,
  Alert,
  Radio,
  Modal,
  Spin,
  Grid,
  Input
} from 'antd';
import {
  GiftOutlined,
  TeamOutlined,
  DollarOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import axios from '../utils/accountApi';
import { useAccountContext } from '../Account';
import type { TourProps } from 'antd';
import LoadingCard from '../components/LoadingCard';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import {
  ACCOUNT_REFERRALS_TOUR_EVENT,
  ACCOUNT_REFERRALS_TOUR_STORAGE_KEY
} from './accountTourEvents';
import './AccountReferrals.css';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = resolveFrontendApiBase();
const REWARDS_PAGE_SIZE = 25;

const formatCredits = (value: number): string => value.toLocaleString();

type ReferralRewardRow = {
  key: string;
  plan: string;
  refereeBonus: number;
  affiliateMonthly: number;
  affiliateAnnual: number;
};

interface ReferralsSectionProps {
  kicker: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
  tourId?: string;
  tone?: 'default' | 'success' | 'info';
}

interface ReferralInfoRowProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
  tone?: 'info' | 'success' | 'warning';
}

interface ReferralShareCardProps {
  label: string;
  value: string;
  description: React.ReactNode;
  buttonText: string;
  onCopy: () => void;
  disabled?: boolean;
  tourId: string;
  tone: 'success' | 'info';
  code?: boolean;
}

const ReferralsModalTitle: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="account-referrals-modal-title">
    <span className="account-referrals-modal-title-icon">{icon}</span>
    <Title level={4}>{title}</Title>
    <Text type="secondary">{subtitle}</Text>
  </div>
);

const ReferralsSection: React.FC<ReferralsSectionProps> = ({
  kicker,
  title,
  description,
  action,
  children,
  tourId,
  tone = 'default',
}) => (
  <section
    className={`account-referrals-section account-referrals-section--${tone}`}
    data-tour-id={tourId}
  >
    <div className="account-referrals-section-header">
      <div className="account-referrals-section-copy">
        <span className="account-referrals-kicker">{kicker}</span>
        <Title level={3}>{title}</Title>
        {description ? <Text type="secondary">{description}</Text> : null}
      </div>
      {action ? <div className="account-referrals-section-action">{action}</div> : null}
    </div>
    <div className="account-referrals-section-body">{children}</div>
  </section>
);

const ReferralInfoRow: React.FC<ReferralInfoRowProps> = ({
  icon,
  title,
  description,
  tone = 'info',
}) => (
  <div className={`account-referrals-info-row account-referrals-info-row--${tone}`}>
    <Avatar className="account-referrals-info-icon" icon={icon} />
    <div className="account-referrals-info-copy">
      <Text strong>{title}</Text>
      <Text type="secondary">{description}</Text>
    </div>
  </div>
);

const ReferralShareCard: React.FC<ReferralShareCardProps> = ({
  label,
  value,
  description,
  buttonText,
  onCopy,
  disabled,
  tourId,
  tone,
  code = false,
}) => (
  <div
    className={`account-referrals-share-card account-referrals-share-card--${tone}`}
    data-tour-id={tourId}
  >
    <div className="account-referrals-share-main">
      <span className="account-referrals-kicker">{label}</span>
      <Text
        className={code ? 'account-referrals-share-value account-referrals-share-value--code' : 'account-referrals-share-value'}
        code={code}
        strong={!code}
      >
        {value}
      </Text>
      <Text type="secondary" className="account-referrals-share-description">
        {description}
      </Text>
    </div>
    <Button type="default" onClick={onCopy} disabled={disabled} className="account-referrals-inline-action">
      {buttonText}
    </Button>
  </div>
);

const ReferralRewardMatrix: React.FC<{ rows: ReferralRewardRow[] }> = ({ rows }) => (
  <ReferralsSection
    kicker="Rewards"
    title="Referral reward matrix"
    description="Plan-based bonuses are issued after the first successful subscription payment."
    tourId="referrals-reward-matrix"
  >
    <div className="account-referrals-table-wrap">
      <table className="account-referrals-reward-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Friend Bonus</th>
            <th>You Earn<br />Monthly billing</th>
            <th>You Earn<br />Annual billing</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={4} className="account-referrals-empty-cell">
                Reward tiers are being updated. Check back soon.
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.key}>
                <td>{row.plan}</td>
                <td>{formatCredits(row.refereeBonus)} credits</td>
                <td>{formatCredits(row.affiliateMonthly)} credits</td>
                <td>{formatCredits(row.affiliateAnnual)} credits</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
    <Text type="secondary" className="account-referrals-table-note">
      Annual billing is handled automatically. Your payout doubles while your referral keeps the same welcome credits.
    </Text>
  </ReferralsSection>
);

export default function AccountReferrals(): JSX.Element {
  const screens = useBreakpoint();
  const {
    usersSharedState,
    referralCodeInfo,
    referralStatusLoading,
    refreshReferralStatus,
    referralStatus
  } = useAccountContext();
  const userID: string = usersSharedState?.userID || '';

  const [isReferralModalVisible, setIsReferralModalVisible] = useState<boolean>(false);
  const [referralCodeOption, setReferralCodeOption] = useState<'generate' | 'custom'>('generate');
  const [customReferralCode, setCustomReferralCode] = useState<string>('');
  const [isGeneratingReferralCode, setIsGeneratingReferralCode] = useState<boolean>(false);
  const [rewardsModalVisible, setRewardsModalVisible] = useState<boolean>(false);
  const [rewardsLoading, setRewardsLoading] = useState<boolean>(false);
  const [rewardsLoadingMore, setRewardsLoadingMore] = useState<boolean>(false);
  const [rewardsError, setRewardsError] = useState<string | null>(null);
  const [rewards, setRewards] = useState<Array<{ id: string; description: string; credits: number; timestamp: string }>>([]);
  const [rewardsPage, setRewardsPage] = useState<number>(0);
  const [rewardsHasMore, setRewardsHasMore] = useState<boolean>(false);
  const [referralsTourOpen, setReferralsTourOpen] = useState<boolean>(false);
  const [referralsTourStep, setReferralsTourStep] = useState<number>(0);
  const [referralsTourFlagsChecked, setReferralsTourFlagsChecked] = useState<boolean>(false);
  const [referralsTourShouldOpen, setReferralsTourShouldOpen] = useState<boolean>(false);
  const [referralsTourSeen, setReferralsTourSeen] = useState<boolean>(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);

  const isAuthenticated: boolean = Boolean(userID);
  const affiliateCode = referralCodeInfo?.referralCode ?? '';
  const hasCode: boolean = useMemo<boolean>(
    () => Boolean((referralCodeInfo?.referralCode || affiliateCode || '').trim().length > 0),
    [referralCodeInfo, affiliateCode]
  );
  const referralRewardMatrix = useMemo(
    () =>
      (referralStatus?.referralRewardMatrix ?? []).map((row) => ({
        key: row.planKey || row.planName,
        plan: row.planName,
        refereeBonus: row.refereeBonus,
        affiliateMonthly: row.affiliateMonthly,
        affiliateAnnual: row.affiliateAnnual
      })),
    [referralStatus]
  );
  const referralLink = useMemo<string>(() => {
    if (!hasCode) return '';
    const encoded = encodeURIComponent(affiliateCode.trim());
    return `https://app.scribeshadow.com/signup?ref=${encoded}`;
  }, [affiliateCode, hasCode]);

  const showInitialLoading = referralStatusLoading && !referralCodeInfo;
  const referralsDataReady = useMemo<boolean>(() => !showInitialLoading, [showInitialLoading]);

  useEffect(() => {
    setReferralsTourFlagsChecked(false);
    setReferralsTourSeen(false);
    setReferralsTourShouldOpen(false);
    setReferralsTourOpen(false);
    setReferralsTourStep(0);
  }, [userID]);

  useEffect(() => {
    if (!userID) return;
    if (referralsTourFlagsChecked) return;
    let cancelled = false;

    const fetchTourFlags = async () => {
      try {
        const res = await axios.get(`${API_BASE}/user/ui-flags`, {
          params: { userId: userID }
        });
        if (cancelled) return;
        const seen = !!res.data?.uiFlags?.accountReferralsTourSeen;
        setReferralsTourSeen(seen);
        if (!seen) {
          setReferralsTourShouldOpen(true);
        }
      } catch (_err) {
        if (cancelled) return;
        setReferralsTourSeen(false);
      } finally {
        if (!cancelled) {
          setReferralsTourFlagsChecked(true);
        }
      }
    };

    fetchTourFlags();
    return () => {
      cancelled = true;
    };
  }, [userID, referralsTourFlagsChecked]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_REFERRALS_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_REFERRALS_TOUR_STORAGE_KEY);
      setReferralsTourShouldOpen(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTourRequest = () => {
      setReferralsTourShouldOpen(true);
    };
    window.addEventListener(ACCOUNT_REFERRALS_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_REFERRALS_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!referralsTourFlagsChecked) return;
    if (!referralsTourShouldOpen) return;
    if (!referralsDataReady) return;
    setReferralsTourOpen(true);
    setReferralsTourStep(0);
    setReferralsTourShouldOpen(false);
  }, [referralsTourFlagsChecked, referralsTourShouldOpen, referralsDataReady]);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement);
      }
      return tourFallbackRef.current as HTMLElement;
    },
    []
  );

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const handleReferralsTourChange = useCallback((nextStep: number) => {
    setReferralsTourStep(nextStep);
  }, []);

  const handleReferralsTourClose = useCallback(async () => {
    setReferralsTourOpen(false);
    setReferralsTourStep(0);
    setReferralsTourShouldOpen(false);
    if (!referralsTourSeen) {
      setReferralsTourSeen(true);
      if (userID) {
        try {
          await axios.post(`${API_BASE}/user/ui-flags/set`, {
            userId: userID,
            flags: { accountReferralsTourSeen: true }
          });
        } catch (_err) {
          // Non-blocking — failure just means we may show the tour again later.
        }
      }
    }
  }, [referralsTourSeen, userID]);

  const referralsTourSteps = useMemo<TourProps['steps']>(() => {
    const targetOrFallback = (primaryId: string, secondaryId?: string) =>
      resolveTourTarget(
        getTourElement(primaryId) ?? (secondaryId ? getTourElement(secondaryId) : null)
      );

    if (hasCode) {
      return [
        {
          title: 'Manage sharing from your toolbox',
          description: renderTeacherPanel(
            <>
              <Text strong>Copy your code, link, and review rewards in one place.</Text>
              <br />
              <Text type="secondary">
                Open the rewards list from this toolbox to see every credit you’ve earned and when payouts landed.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-toolbox', 'referrals-main-card')
        },
        {
          title: 'Review earned rewards',
          description: renderTeacherPanel(
            <>
              <Text strong>Open the rewards list to audit referral credits.</Text>
              <br />
              <Text type="secondary">
                It shows qualifying payouts after eligible first payments clear.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-rewards-button', 'referrals-toolbox')
        },
        {
          title: 'Share your referral code',
          description: renderTeacherPanel(
            <>
              <Text strong>Highlight the code on stream or in posts.</Text>
              <br />
              <Text type="secondary">
                Use the code when links are awkward, like social bios or overlays.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-code-card', 'referrals-toolbox')
        },
        {
          title: 'Send friends a tracking link',
          description: renderTeacherPanel(
            <>
              <Text strong>Share the link for automatic tracking.</Text>
              <br />
              <Text type="secondary">
                Links track signups automatically. Rewards arrive after a friend’s first eligible payment.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-link-card', 'referrals-toolbox')
        },
        {
          title: 'Understand the payout table',
          description: renderTeacherPanel(
            <>
              <Text strong>See exactly what everyone earns.</Text>
              <br />
              <Text type="secondary">
                The matrix shows rewards per plan, including annual bonuses and when each payout qualifies.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-reward-matrix', 'referrals-toolbox')
        }
      ];
    }

    return [
        {
          title: 'Invite writers and earn credits',
          description: renderTeacherPanel(
            <>
              <Text strong>Create your referral code to unlock rewards.</Text>
              <br />
              <Text type="secondary">
                Friends receive bonus credits; you earn rewards after their first eligible payment.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-callout', 'referrals-main-card')
        },
        {
          title: 'See how the program works',
          description: renderTeacherPanel(
            <>
              <Text strong>Follow the quick overview.</Text>
              <br />
              <Text type="secondary">
                Generate a code, share it or the link, and track rewards after eligible first payments clear.
              </Text>
            </>
          ),
          target: () => targetOrFallback('referrals-overview-cards', 'referrals-callout')
        },
      {
        title: 'Generate your referral code',
        description: renderTeacherPanel(
          <>
            <Text strong>Choose a custom code or let us create one.</Text>
            <br />
            <Text type="secondary">
              Start sharing immediately—rewards trigger after a friend’s first subscription payment.
            </Text>
          </>
        ),
        target: () => targetOrFallback('referrals-generate-button', 'referrals-main-card')
      }
    ];
  }, [getTourElement, hasCode, renderTeacherPanel, resolveTourTarget]);

  const handleGenerateReferralCode = async (customCode: string | null = null) => {
    if (!userID) {
      message.error('You must be signed in to generate a referral code.');
      return;
    }
    setIsGeneratingReferralCode(true);
    try {
      await axios.post<{ referralCode: string }>(
        `${API_BASE}/generate-referral-code`,
        { userID, customCode }
      );
      setIsReferralModalVisible(false);
      setCustomReferralCode('');
      setReferralCodeOption('generate');
      message.success('Referral code generated successfully.');
      await refreshReferralStatus();
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response) {
        const detail = String((error.response.data as { detail?: string })?.detail || '').toLowerCase();
        if (error.response.status === 400 && detail.includes('already exists')) {
          message.error('This custom code is already in use. Please choose a different one.');
        } else if (error.response.status === 400 && detail.includes('invalid')) {
          message.error('Invalid code. Use letters and numbers only, up to 15 characters.');
        } else {
          message.error('Failed to generate referral code. Please try again.');
        }
      } else {
        message.error('Failed to generate referral code. Please try again.');
      }
    } finally {
      setIsGeneratingReferralCode(false);
    }
  };

  const onCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(affiliateCode);
      message.success('Referral code copied to clipboard.');
    } catch {
      message.error('Could not copy referral code.');
    }
  };

  const onCopyLink = async () => {
    if (!referralLink) {
      message.error('Referral link unavailable.');
      return;
    }
    try {
      await navigator.clipboard.writeText(referralLink);
      message.success('Referral link copied to clipboard.');
    } catch {
      message.error('Could not copy referral link.');
    }
  };

  const fetchReferralRewards = useCallback(
    async (pageToLoad: number) => {
      if (!userID) return;
      const isFirstPage = pageToLoad === 1;
      if (isFirstPage) {
        setRewardsLoading(true);
        setRewardsError(null);
        setRewards([]);
        setRewardsPage(0);
        setRewardsHasMore(false);
      } else {
        setRewardsLoadingMore(true);
      }

      try {
        const { data } = await axios.get<{
          transactions?: Array<{
            id?: string;
            description?: string;
            credits?: number;
            timestamp?: string;
            type?: string;
          }>;
          hasMore?: boolean;
        }>(`${API_BASE}/credit-history`, {
          params: {
            userID,
            page: pageToLoad,
            page_size: REWARDS_PAGE_SIZE,
            referral_only: true,
            include_filters: false
          }
        });

        const rawTransactions = Array.isArray(data.transactions) ? data.transactions : [];
        const newEntries = rawTransactions
          .map((tx) => ({
            id: tx.id || `${tx.timestamp || ''}-${tx.description || ''}-${tx.type || ''}`,
            description: tx.description || 'Referral reward',
            credits: Number(tx.credits ?? 0),
            timestamp: tx.timestamp || ''
          }))
          .filter((entry) => Number.isFinite(entry.credits) && entry.credits > 0);

        setRewards((prev) => {
          if (isFirstPage) {
            return newEntries;
          }
          const seen = new Set(prev.map((item) => item.id));
          const combined = [...prev];
          newEntries.forEach((entry) => {
            if (!seen.has(entry.id)) {
              combined.push(entry);
              seen.add(entry.id);
            }
          });
          return combined;
        });

        setRewardsPage(pageToLoad);
        setRewardsHasMore(Boolean(data?.hasMore));
      } catch (error) {
        console.error('Failed to load referral rewards', error);
        if (isFirstPage) {
          setRewards([]);
          setRewardsError('Unable to load referral rewards right now.');
        } else {
          message.error('Unable to load more referral rewards right now.');
        }
      } finally {
        if (isFirstPage) {
          setRewardsLoading(false);
        } else {
          setRewardsLoadingMore(false);
        }
      }
    },
    [userID]
  );

  const handleLoadMoreRewards = useCallback(async () => {
    if (!userID || rewardsLoading || rewardsLoadingMore || !rewardsHasMore) return;
    try {
      await fetchReferralRewards(rewardsPage + 1);
    } catch {
      // handled inside fetchReferralRewards
    }
  }, [userID, rewardsLoading, rewardsLoadingMore, rewardsHasMore, rewardsPage, fetchReferralRewards]);

  const openRewardsModal = useCallback(async () => {
    if (!userID) return;
    setRewardsModalVisible(true);
    await fetchReferralRewards(1);
  }, [userID, fetchReferralRewards]);

  const rewardsTotal = useMemo(
    () => rewards.reduce((sum, entry) => sum + (Number.isFinite(entry.credits) ? entry.credits : 0), 0),
    [rewards]
  );

  const formatRewardDate = (timestamp: string) => {
    if (!timestamp) return 'Unknown date';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return timestamp;
    return date.toLocaleString();
  };

  return (
    <div className="account-referrals-shell">
      {!isAuthenticated && (
        <Alert
          type="info"
          showIcon
          message="You are not signed in"
          description="Sign in to generate and share your referral code."
          className="account-referrals-alert"
        />
      )}

      <Card
        bordered
        className="account-referrals-card"
        title={
          <div className="account-referrals-card-title">
            <span className="account-referrals-kicker">Referrals</span>
            <Title level={2}>Referral Program</Title>
            <Text type="secondary">Share a tracked code or link and earn forever credits.</Text>
          </div>
        }
        data-tour-id="referrals-main-card"
      >
        {showInitialLoading ? (
          <div className="account-referrals-loading">
            <LoadingCard label="Loading referral details..." />
          </div>
        ) : hasCode ? (
          <div className="account-referrals-stack">
            <ReferralsSection
              kicker="Sharing"
              title="Your Referral Toolbox"
              description="Copy your code or signup link. Friends receive bonus credits after their first paid subscription starts, and you earn the listed affiliate reward."
              action={(
                <Button type="default" onClick={openRewardsModal} className="account-referrals-inline-action" data-tour-id="referrals-rewards-button">
                  View rewards earned
                </Button>
              )}
              tourId="referrals-toolbox"
              tone="info"
            >
              <div className="account-referrals-share-grid">
                <ReferralShareCard
                  label="Referral code"
                  value={affiliateCode}
                  description="Use this in emails, posts, messages, bios, or overlays."
                  buttonText="Copy code"
                  onCopy={onCopyCode}
                  tourId="referrals-code-card"
                  tone="success"
                  code
                />
                <ReferralShareCard
                  label="Signup link"
                  value={referralLink || 'Generating...'}
                  description="Send this link so signup tracking happens automatically."
                  buttonText="Copy link"
                  onCopy={onCopyLink}
                  disabled={!referralLink}
                  tourId="referrals-link-card"
                  tone="info"
                />
              </div>
            </ReferralsSection>

            <div className="account-referrals-detail-grid">
              <ReferralsSection
                kicker="How it works"
                title="Program details"
                description="Referral rewards are automatic for eligible new customers."
              >
                <div className="account-referrals-info-list">
                  <ReferralInfoRow
                    icon={<TeamOutlined />}
                    title="Friends start with bonus credits"
                    description="New customers receive a one-time forever credit grant matched to their plan tier once their first payment clears."
                    tone="info"
                  />
                  <ReferralInfoRow
                    icon={<DollarOutlined />}
                    title="You earn forever credits"
                    description="After their first payment, you receive the affiliate reward shown in the table. Annual subscriptions double your payout."
                    tone="success"
                  />
                  <ReferralInfoRow
                    icon={<CheckCircleOutlined />}
                    title="New customers only"
                    description="Rewards apply to eligible new customers on Storyteller through Polyglot and are issued automatically."
                    tone="warning"
                  />
                </div>
              </ReferralsSection>

              <ReferralRewardMatrix rows={referralRewardMatrix} />
            </div>
          </div>
        ) : (
          <div className="account-referrals-activation">
            <ReferralsSection
              kicker="Start sharing"
              title="Create referral code"
              description="Generate a random code or choose one that fits your author brand."
              tourId="referrals-callout"
              tone="success"
            >
              <div className="account-referrals-overview-grid" data-tour-id="referrals-overview-cards">
                <ReferralInfoRow
                  icon={<GiftOutlined />}
                  title="Generate your code"
                  description="Create a random or custom code in seconds."
                  tone="info"
                />
                <ReferralInfoRow
                  icon={<TeamOutlined />}
                  title="Share with friends"
                  description="They receive bonus credits after their first paid subscription starts."
                  tone="warning"
                />
                <ReferralInfoRow
                  icon={<DollarOutlined />}
                  title="Earn forever credits"
                  description="Get plan-based rewards. Annual plans double your payout."
                  tone="success"
                />
              </div>

              <div className="account-referrals-primary-action">
                <Button
                  type="primary"
                  icon={<GiftOutlined />}
                  onClick={() => setIsReferralModalVisible(true)}
                  size="large"
                  block
                  disabled={!isAuthenticated}
                  data-tour-id="referrals-generate-button"
                >
                  Generate My Referral Code
                </Button>
                <Text type="secondary">
                  {isAuthenticated
                    ? 'Rewards are issued to both accounts after the first successful subscription payment. New customers only.'
                    : 'Sign in to generate a referral code.'}
                </Text>
              </div>
            </ReferralsSection>
          </div>
        )}
      </Card>

      <AppTour
        open={referralsTourOpen}
        steps={referralsTourSteps}
        current={referralsTourStep}
        onChange={handleReferralsTourChange}
        onClose={handleReferralsTourClose}
        onFinish={handleReferralsTourClose}
      />

      <Modal
        title={
          <ReferralsModalTitle
            icon={<GiftOutlined />}
            title="Generate Referral Code"
            subtitle="Create a random code or choose your own"
          />
        }
        open={isReferralModalVisible}
        onCancel={() => setIsReferralModalVisible(false)}
        footer={null}
        centered
        className="account-referrals-modal"
      >
        <div className="account-referrals-modal-body">
          <div className="account-referrals-modal-block account-referrals-modal-block--muted">
            <Text>
              Friends receive bonus forever credits after their first paid subscription starts. You earn the affiliate
              reward listed for their plan, doubled on annual billing.
            </Text>
          </div>

          <div className="account-referrals-modal-block">
            <div className="account-referrals-modal-block-header">
              <Title level={5}>Code option</Title>
              <Text type="secondary">Let ScribeShadow generate a code or enter your own.</Text>
            </div>
            <Radio.Group
              onChange={(e) => setReferralCodeOption(e.target.value)}
              value={referralCodeOption}
              className="account-referrals-radio-group"
              disabled={!isAuthenticated}
            >
              <Space direction="vertical" className="account-referrals-radio-list">
                <Radio value="generate">Generate random code</Radio>
                <Radio value="custom">Choose my own code</Radio>
              </Space>
            </Radio.Group>
          </div>

          {referralCodeOption === 'generate' ? (
            <Button
              type="primary"
              onClick={() => handleGenerateReferralCode()}
              loading={isGeneratingReferralCode}
              className="account-referrals-modal-primary"
              disabled={!isAuthenticated}
            >
              Generate Random Code
            </Button>
          ) : (
            <div className="account-referrals-modal-block">
              <Input
                placeholder="Enter custom code (letters and numbers only)"
                value={customReferralCode}
                onChange={(e) => {
                  const sanitized: string = e.target.value
                    .toUpperCase()
                    .replace(/[^A-Z0-9]/g, '')
                    .slice(0, 15);
                  setCustomReferralCode(sanitized);
                }}
                className="account-referrals-code-input"
                disabled={!isAuthenticated}
              />
              <Text type="secondary" className="account-referrals-field-helper">
                Choose something memorable such as your name or pen name. Max 15 characters.
              </Text>
              <Button
                type="primary"
                onClick={() => handleGenerateReferralCode(customReferralCode)}
                loading={isGeneratingReferralCode}
                disabled={!customReferralCode || !isAuthenticated}
                className="account-referrals-modal-primary"
              >
                Create Custom Code
              </Button>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        title={
          <ReferralsModalTitle
            icon={<DollarOutlined />}
            title="Referral rewards"
            subtitle="Review credits earned from successful referrals"
          />
        }
        open={rewardsModalVisible}
        onCancel={() => setRewardsModalVisible(false)}
        footer={null}
        centered
        className="account-referrals-modal"
        width={screens.md ? 520 : '90%'}
      >
        {rewardsLoading ? (
          <div className="account-referrals-modal-loading">
            <Spin />
            <Text type="secondary">Loading referral rewards...</Text>
          </div>
        ) : rewardsError ? (
          <div className="account-referrals-modal-body">
            <div className="account-referrals-modal-block">
              <Alert type="error" showIcon message={rewardsError} />
              <Button
                type="default"
                block
                onClick={() => fetchReferralRewards(1)}
                className="account-referrals-inline-action account-referrals-modal-retry"
              >
                Try again
              </Button>
            </div>
          </div>
        ) : rewards.length === 0 ? (
          <div className="account-referrals-modal-body">
            <div className="account-referrals-modal-block account-referrals-empty-rewards">
              <GiftOutlined className="account-referrals-empty-rewards-icon" />
              <div>
                <Text strong>No referral rewards yet</Text>
                <Text type="secondary">
                  Invite friends using your referral code. Credits appear here after their first successful subscription payment.
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <div className="account-referrals-modal-body account-referrals-rewards-body">
            <div className="account-referrals-total-surface">
              <span className="account-referrals-kicker">Total earned</span>
              <Text strong>
                {rewardsTotal.toLocaleString()} credits
              </Text>
              <Text type="secondary">
                Rewards are issued after your referral’s first successful subscription payment.
              </Text>
            </div>

            <div className="account-referrals-modal-block">
              <div className="account-referrals-modal-block-header">
                <Title level={5}>Reward history</Title>
                <Text type="secondary">Newest referral credit grants appear first.</Text>
              </div>
              <div className="account-referrals-reward-list">
                {rewards.map((entry) => (
                  <Card key={entry.id} size="small" className="account-referrals-reward-entry">
                    <div className="account-referrals-reward-entry-body">
                      <div className="account-referrals-reward-entry-copy">
                        <Text strong>{entry.description}</Text>
                        <Text type="secondary">{formatRewardDate(entry.timestamp)}</Text>
                      </div>
                      <Text className="account-referrals-credit-pill">
                        {entry.credits.toLocaleString()} credits
                      </Text>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {rewardsHasMore && (
              <Button
                type="default"
                block
                onClick={handleLoadMoreRewards}
                loading={rewardsLoadingMore}
                disabled={rewardsLoadingMore}
                className="account-referrals-inline-action"
              >
                Load more rewards
              </Button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
